/**
 * iter90 · Strategia C · Section prefetch orchestrator.
 *
 * Two prefetch flows coexist here (see /app/memory/perf_diagnosis.md
 * "Section Navigation Diagnosis pass 4 · Q6 Strategy C"):
 *
 *   1. IDLE (Home boot): after LCP + requestIdleCallback with 3000ms
 *      timeout, we
 *        a) trigger the webpack chunk fetch for SectionPage via a
 *           dynamic `import()` (no need to hardcode the hashed URL —
 *           webpack resolves the current chunk name at build time),
 *        b) warm-fetch `/api/sections/{slug}` for the three HOT slugs
 *           (Bio, My Vision, My Journey) into apiCache.
 *      Guarded by `shouldPrefetch()` — respects Save-Data + 2g/slow-2g.
 *
 *   2. INTENT (menu hover / focus / touchstart): fetches only the
 *      section-data of the hovered link. Used for Materia + Portfolio
 *      (not idle-prefetched to keep the "cost" low; see brief).
 *      Dedupe is automatic via apiCache in-flight logic.
 *
 * Neither flow prefetches videos, portfolio-items galleries, or heavy
 * images — those stay lazy per the user's constraint.
 */

import { api, API } from './api';
import apiCache from './apiCache';

const HOT_SLUGS = ['cinzia-bio', 'cinzia-vision', 'cinzia-journey'];

/**
 * Global guard. Returns false when Save-Data is on or the network is
 * 2g/slow-2g. Any prefetch caller must respect this.
 */
export function shouldPrefetch() {
    if (typeof navigator === 'undefined') return false;
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return true; // no NetworkInformation API → don't block
    if (conn.saveData === true) return false;
    if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') return false;
    return true;
}

/**
 * Trigger the webpack chunk download for SectionPage. Uses the same
 * dynamic import expression as App.js (webpack will emit the SAME
 * chunk, so the file lands in disk cache and the later Suspense
 * resolves synchronously).
 */
export function prefetchSectionChunk() {
    if (!shouldPrefetch()) return Promise.resolve();
    if (typeof window === 'undefined') return Promise.resolve();
    // Idempotent: webpack caches the module Promise internally.
    return import(/* webpackChunkName: "src_pages_SectionPage_jsx" */ '../pages/SectionPage')
        .catch((e) => {
            // Chunk download failed (offline / stale hash after a
            // deploy). Non-fatal — the click-time Suspense will just
            // do the fetch normally.
            console.debug('[sectionPrefetch] chunk fetch failed:', e);
        });
}

/**
 * Warm-fetch `/api/sections/{slug}` for a given lang. Delegates to
 * `api.getSection()` which already routes through apiCache — this
 * guarantees the cache key (URL) and axios headers match EXACTLY
 * what the SectionPage useEffect will produce at click time. Any
 * mismatch (query serialization order, encoding, Accept headers)
 * would cause a cache miss.
 */
export function prefetchSectionData(slug, lang = 'it') {
    if (!slug || !shouldPrefetch()) return Promise.resolve(null);
    return api.getSection(slug, lang).catch((e) => {
        console.debug('[sectionPrefetch] data fetch failed for', slug, e);
        return null;
    });
}

/**
 * Orchestrator: called from Home after mount + LCP fires.
 * Chains chunk prefetch → HOT slug data prefetch, both inside
 * requestIdleCallback so we never contend with critical rendering.
 *
 * Returns a cleanup function usable in `useEffect`.
 */
export function initIdlePrefetch(lang = 'it') {
    if (typeof window === 'undefined') return () => {};
    if (!shouldPrefetch()) return () => {};

    const timers = [];
    const ricHandles = [];

    const idle = (fn, timeout) => {
        if (typeof window.requestIdleCallback === 'function') {
            const h = window.requestIdleCallback(fn, { timeout });
            ricHandles.push(h);
        } else {
            const t = setTimeout(fn, Math.min(timeout, 3000));
            timers.push(t);
        }
    };

    // Wait for LCP before scheduling — this avoids racing the poster/
    // sneaker LCP paint. Falls back to a 3s timer if PerformanceObserver
    // LCP is unavailable or never fires.
    let started = false;
    const start = () => {
        if (started) return;
        started = true;
        // Step 1: chunk (150-200 KB, priority Lowest via dynamic import).
        idle(() => { prefetchSectionChunk(); }, 3000);
        // Step 2: HOT slugs data (small JSON, ~5-20 KB each). Scheduled
        // in a second idle so the chunk gets first bite of bandwidth.
        idle(() => {
            HOT_SLUGS.forEach((slug) => prefetchSectionData(slug, lang));
        }, 5000);
    };

    let po = null;
    try {
        if ('PerformanceObserver' in window && typeof PerformanceObserver === 'function') {
            po = new PerformanceObserver(() => {
                start();
                try { po.disconnect(); } catch (e) {}
            });
            po.observe({ type: 'largest-contentful-paint', buffered: true });
        }
    } catch (e) {
        /* fall through to timer */
    }
    // Safety net: if LCP never fires (SPA hard case), start after 3s.
    const safety = setTimeout(start, 3000);
    timers.push(safety);

    return () => {
        try { if (po) po.disconnect(); } catch (e) {}
        timers.forEach(clearTimeout);
        if (typeof window.cancelIdleCallback === 'function') {
            ricHandles.forEach((h) => {
                try { window.cancelIdleCallback(h); } catch (e) {}
            });
        }
    };
}
